/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useMemo, type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import ImageGallery from '@/components/image-gallery';
import ProductInfo from './product-info';
import ProductCartActions from '@/components/product-cart-actions';
import ProductViewProvider from '@/providers/product-view';
import { useProductImages } from '@/hooks/product/use-product-images';
import { useSelectedVariations } from '@/hooks/product/use-selected-variations';
import { isProductSet, isProductBundle } from '@/lib/product/product-utils';
import HtmlFragment from '@/components/html-fragment';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Icon } from '@/components/icon';
import ProductSpecifications, { ProductSpecificationsFallback } from '@/components/product-specifications';
import { computeSpecifications } from '@/lib/product/specifications';
import ProductVariationsTable, { ProductVariationsTableFallback } from '@/components/product-variations-table';
import type { ProductOrderData } from '@/lib/product/variations-table';
import { filterVariationRows, readActiveFilters } from '@/lib/product/variation-filters';

interface ProductViewProps {
    product: ShopperProducts.schemas['Product'];
    mode?: 'add' | 'edit';
    /** Deferred master `c_model-code` for the identifier line; omitted, `<ProductInfo>` uses `product.id`. */
    modelCodePromise?: Promise<string | null>;
    /**
     * Master `c_model-name-for-web` for the `<h1>`. A plain string, not a promise — the loader awaits
     * it because a heading is critical data. `null` and omitted both mean "use `product.name`".
     */
    modelName?: string | null;
    /** Deferred Features copy for the Details panel; omitted or `null`, the block is absent. */
    featuresPromise?: Promise<string | null>;
    /**
     * The Order band's single deferred resolution, feeding BOTH the specifications panel and the
     * variations table. Omitted, neither renders — which is what the modals get: they compose
     * `ProductInfo` without this shell.
     */
    orderPromise?: Promise<ProductOrderData>;
}

/**
 * Stopgap Service destination: one URL from the CRCB2B-313 ticket, used for EVERY product.
 *
 * The real destination is per-product and its PIM attribute does not arrive on the SCAPI payload
 * yet (CRCB2B-313 follow-up). Kept as a named constant so landing that attribute is a one-line
 * swap. Known and accepted limitation, not a defect to file.
 */
const SERVICE_TAB_PLACEHOLDER_HREF = 'https://www.sram.com/en/service/models/rd-xx-e-b1';

/**
 * Streams the resolved Features copy into the Details panel.
 *
 * `use()` rather than `<Await>` so this stays usable outside a data-router context; `resolveFeatures`
 * owns the `.catch()`, so a failed master fetch arrives as `null`, never as a rejection.
 *
 * The copy reaches `dangerouslySetInnerHTML` unsanitized — `HtmlFragment` does not sanitize, and
 * nothing in this repo does. Safe while the field is merchant-authored in Business Manager, the same
 * trust the PDP already extends to SCAPI's `longDescription`; NOT safe if it ever becomes shopper-
 * or third-party-writable.
 *
 * `className` REPLACES the contentType styles rather than extending them, which is why
 * `whitespace-pre-line` is restated here — drop it and the PIM textarea's `\n`s collapse into one
 * paragraph. The rest of the class list is Figma `body/lg`; `consumers.spec.json` asserts the
 * computed values, including the two that no static check can see.
 */
function ProductFeatures({ promise }: { promise: Promise<string | null> }): ReactNode {
    const features = use(promise);

    if (!features) return null;

    return (
        // No margin: the column spaces its children with a gap, and an absent block costs no gap
        // because it renders no node at all.
        <div>
            <HtmlFragment
                content={features}
                contentType="plain-text-preserve-lines"
                className="text-lg leading-normal tracking-sram text-foreground whitespace-pre-line"
            />
        </div>
    );
}

/**
 * The Specifications disclosure — heading, chevron and panel, with no opinion on the content.
 *
 * Extracted because the resolved section and its loading state must render the SAME shell: the shell is
 * what reserves height while the rows resolve, so restating it in the fallback would let the two drift.
 *
 * The accordion header, trigger, `variant="white"` and expanded default are final — do not restyle.
 * `focus-visible:outline-solid` mitigates the shared-primitive outline bug in place, per instance,
 * retired by `focus-visible-indicators`.
 */
function SpecificationsDisclosure({ label, children }: { label: string; children: ReactNode }): ReactElement {
    return (
        <Accordion type="single" collapsible defaultValue="specifications">
            <AccordionItem value="specifications" variant="white" data-testid="product-specifications">
                <AccordionTrigger className="focus-visible:outline-solid">{label}</AccordionTrigger>
                <AccordionContent>{children}</AccordionContent>
            </AccordionItem>
        </Accordion>
    );
}

/**
 * Specifications, gated on having any.
 *
 * `use()` runs OUTSIDE the disclosure, and that placement is the whole point: where the catalogue has
 * no answer the section is ABSENT — no heading, no empty panel, no explanatory copy. There is no
 * fallback list to put in a panel, so a labelled empty box would read as a defect on every instance
 * where the attribute-model endpoint is not yet provisioned. Gating INSIDE `AccordionContent` cannot
 * express that: by then the heading has already rendered.
 *
 * The cost is a layout shift in exactly that case — the fallback reserves height, then the section
 * disappears. It is accepted: the shift lands only where the section should not exist, the panel is
 * non-critical so the rows cannot be awaited to learn the answer sooner, and the alternative is a
 * permanently empty disclosure on every unprovisioned environment.
 */
function ProductSpecificationsSection({
    promise,
    label,
}: {
    promise: Promise<ProductOrderData>;
    label: string;
}): ReactNode {
    // `failure` arrives on the SAME promise the rows do, because the panel and the table are one
    // deferred resolution. No second channel is threaded for it: adding a prop beside `orderPromise`
    // would give the page two sources for one fact and let them disagree.
    const { model, specificationAttributes, specificationRows, failure } = use(promise);
    const [searchParams] = useSearchParams();

    // The panel follows the table's filters. Narrowing the table to `Body Eyelet: Standard` while this
    // panel still reads `Standard, Trunnion` states two contradictory things about one model on one
    // screen.
    //
    // This is NOT the selected-variant coupling the panel forbids, and the difference is what makes it
    // safe: a filter narrows the SET, and the projection is defined as a function of the set it is
    // given. No selection is read, held or subscribed to.
    //
    // The unfiltered rows are used as-is rather than recomputed, so the common path returns exactly
    // what the loader produced and the move to the client is invisible.
    const rows = useMemo(() => {
        const filters = readActiveFilters(model, searchParams);

        if (!filters.length) return specificationRows;

        return computeSpecifications(
            filterVariationRows(model, filters).map((row) => row.product),
            specificationAttributes
        );
    }, [model, searchParams, specificationAttributes, specificationRows]);

    /**
     * A master with no declared variants renders NO panel, whatever rows the projection produced.
     *
     * Specifications are a union across the variant set. With no variants, the only member the union has
     * to work with is the MASTER — which carries a sparse subset of what its variants would, so the
     * panel would state a partial specification of the model as if it were the whole one. That is worse
     * than absence: a reader cannot tell a short list from a complete one.
     *
     * Only this cause. A transport failure and unresolved declared variants are TRANSIENT — the variant
     * data may well be there on the next request — so they keep the panel's ordinary behaviour rather
     * than being permanently suppressed on the strength of one bad response.
     */
    if (failure?.reason === 'master-has-no-variants') return null;

    if (!rows.length) return null;

    return (
        <SpecificationsDisclosure label={label}>
            {/* One `label` reaches both the trigger and the list — the desktop panel is a focusable
                scroll region and needs an accessible name, and taking it from the visible heading keeps
                the two from drifting. */}
            <ProductSpecifications rows={rows} label={label} />
        </SpecificationsDisclosure>
    );
}

/**
 * Unwraps the order table's promise inside the Suspense boundary.
 *
 * A separate component for the same reason `ProductSpecificationsSection` is one: `use()` must be
 * called BELOW the boundary, not beside it, or the whole view suspends instead of the section.
 */
function ProductVariationsSection({
    promise,
    product,
}: {
    promise: Promise<ProductOrderData>;
    product: ShopperProducts.schemas['Product'];
}): ReactNode {
    const { model, specificationAttributes, truncation, failure } = use(promise);

    /*
     * A failed set must not take the page's purchase path with it. The band is an either/or and the
     * table's failure notice is not a way to buy anything, so on a variant url a hiccup in the deferred,
     * non-critical `getProducts` left a page whose critical data loaded fine with nothing to add.
     *
     * Keyed on the product being orderable in its own right rather than on the failure reason. Neither a
     * MASTER nor a VARIATION GROUP id can be added to a basket, so for those the notice genuinely is the
     * whole of what the band can say — which is the case `master-has-no-variants` describes.
     *
     * This does not reopen the two-purchase-paths question the band settled: the fallback replaces a
     * table that rendered no action, so the page still offers exactly one.
     */
    if (failure && !product.type?.master && !product.type?.variationGroup) {
        return <ProductCartActions product={product} hideExpressPayments hideOptionsMessage />;
    }

    // The same list the panel unions across the set. Given a set of one it describes a single member,
    // which is exactly what an expanded row needs.
    //
    // `failure` travels with them rather than being handled here: the table owns what the band renders,
    // and an unresolvable set is one of its states rather than an absence of it.
    return (
        <ProductVariationsTable
            model={model}
            specificationAttributes={specificationAttributes}
            truncation={truncation}
            failure={failure}
        />
    );
}

/** Tab values are stable identifiers, not display strings — labels come from i18n. */
const TAB_DETAILS = 'details';

/**
 * ProductView component renders a complete product detail view with image gallery and product information.
 *
 * @param props - The component props
 * @param props.product - The product data from Salesforce Commerce Cloud containing all product details,
 *                        variants, pricing, and metadata
 *
 * @returns A React element containing the complete product view layout
 *
 * @example
 * ```tsx
 * <ProductView product={productData} />
 * ```
 */
export default function ProductView({
    product,
    modelCodePromise,
    modelName,
    featuresPromise,
    orderPromise,
}: ProductViewProps): ReactElement {
    // Calculate directly without useMemo since these are simple operations
    const isProductASet = isProductSet(product);
    const isProductABundle = isProductBundle(product);

    // Get selected attributes from URL parameters for image gallery
    const selectedAttributes = useSelectedVariations({ product });
    const { galleryImages } = useProductImages({
        product,
        selectedAttributes,
    });

    const { t } = useTranslation('product');

    return (
        <ProductViewProvider product={product} mode="add">
            {/* The route supplies the same gap-4 above, between breadcrumbs and this block, so
                breadcrumbs, header and tab bar read as one 16px column. */}
            <div className="grid gap-4">
                {/* Identity only — the B2B PDP supplies its own multi-variant order grid, so
                    <ProductPurchasePanel> is not rendered here. A surface that needs the purchase
                    affordance composes both, as the modals do.

                    Every flag here is an opt-out from a default that SHOWS, so the modals — which
                    render the same component — are unaffected. `variantStyle="pdp"` is likewise a
                    distinct style rather than a change to `full`, which they share. */}
                <ProductInfo
                    product={product}
                    variantStyle="pdp"
                    hideActionIcons
                    hideDescription
                    showBrandLogo
                    modelCodePromise={modelCodePromise}
                    modelName={modelName}
                    // @sfdc-extension-line SFDC_EXT_RATINGS_REVIEWS
                    hideRating
                />

                {/* gap-8 per the PDP frame, overriding the primitive's gap-6 — its own doc marks that
                    value DERIVED, since Figma's Tabs node defines no rail-to-content spacing. */}
                {/* `min-w-0` is load-bearing, not defensive. This is a GRID ITEM of the `grid gap-4`
                    above, and a grid item's automatic minimum size is its content's min-content width —
                    so the panel's widest descendant sets the column's floor. The variations table is
                    wider than a phone viewport by design and scrolls inside its own container, but
                    `w-full` on that container is a PERCENTAGE, and percentages resolve as `auto` during
                    intrinsic sizing. Without this the table's full width reaches the column, the column
                    outgrows the page, and every block on the panel renders at table width while the
                    header and footer stay at device width. */}
                <Tabs defaultValue={TAB_DETAILS} className="min-w-0 gap-8">
                    {/* The row's full-width base divider, which the primitive cannot supply — it composes
                        the rail from each trigger's `::after`, so the line would stop at the last item.

                        Two non-obvious constraints. It must live INSIDE the row, not as a `border-b`
                        below it: `TabsList` carries `overflow-y-hidden`, so anything painted below a
                        trigger's own box is clipped, and an active bar reaching 1px lower to cover a
                        border loses that 1px. And it must be `::before`, not `::after` — `::after` is
                        the last child, so it paints OVER the triggers and greys out the active bar.

                        gap-0 because Figma's horizontal spacing is each item's own px-4; a container gap
                        stacks on top of it (40px measured, not 32px). */}
                    <div className="relative flex items-center gap-0 before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-border">
                        <TabsList>
                            {/* Geometry and type are the primitive's, untouched. Only the resting rail is
                                silenced: the divider above is the base line now, so the primitive's own
                                1px would paint directly on top of it and read as 2px under inactive tabs.
                                Hover still thickens — that rule is more specific than this reset. */}
                            {/* LOCAL a11y MITIGATION — `focus-visible:outline-solid`.
                                In Tailwind v4 `outline-none` sets `--tw-outline-style: none`, and
                                `outline-2` resolves its style from that variable, so the primitive's
                                `outline-none focus-visible:outline-2` computes to `outline-style: none;
                                outline-width: 0px` — colour and offset still apply, which is why it reads
                                as correct in review. Measured on this page, not reasoned.

                                Applied per instance because the bug lives in the SHARED primitive and
                                fixing it there would ripple across every surface. Delete these overrides
                                when `openspec/changes/focus-visible-indicators` lands; that change's task 5.1
                                names them. Debt with a known payoff date. */}
                            <TabsTrigger
                                value={TAB_DETAILS}
                                className="data-[state=inactive]:after:h-0 focus-visible:outline-solid">
                                {t('tabs.details')}
                            </TabsTrigger>
                        </TabsList>

                        {/* Rendered outside <TabsList> because a `role="tablist"` may only contain
                            `role="tab"` children — a link with that role would announce a panel that does
                            not exist and lose Enter, middle-click and "open in new tab". As a sibling it
                            stays a real link and is reachable by Tab in visual order.

                            Nothing from the primitive reaches it, so the box, type and hover treatment are
                            copied from TabsTrigger in ui/tabs.tsx — keep them in sync. Its `::after` rests
                            at h-0 (the row's own `::before` draws the base line for the full width) and
                            thickens to 2px on hover. It never takes the active bar's black: an outbound
                            link is never a selected tab. */}
                        <a
                            href={SERVICE_TAB_PLACEHOLDER_HREF}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t('tabs.serviceOpensInNewTab')}
                            className="relative inline-flex h-11 shrink-0 items-center gap-2 rounded-ui px-4 py-3 text-lg leading-normal font-bold tracking-[0.03em] whitespace-nowrap text-text-tertiary transition-colors after:absolute after:inset-x-0 after:bottom-0 after:h-0 after:bg-border after:transition-all outline-none hover:text-muted-foreground hover:after:h-0.5 hover:after:bg-border-hover focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2">
                            {t('tabs.service')}
                            {/* `icon/default` — 20px, per the header frame (`13546:37027`), where the
                                glyph measures exactly 20x20. Sized with a font-size class rather than
                                Icon's `size` prop: the prop lands inline and would beat any later CSS,
                                which is the rule tabs.tsx already follows for its own glyphs. */}
                            <Icon name="open_in_new" aria-hidden className="text-[20px]" />
                        </a>
                    </div>

                    {/* Radix makes the panel focusable (`tabindex=0`) so its content is reachable, but
                        `ui/tabs.tsx` defines no focus treatment for it — a different defect from the two
                        above, same LOCAL mitigation. Delete with
                        `focus-visible-indicators`. */}
                    <TabsContent
                        value={TAB_DETAILS}
                        // The panel stacks its blocks with a GAP, matching how the design's own frame is
                        // built: `var(--spacing-10)` on the 399px frame and `var(--spacing-8)` on the
                        // 1376px one. `lg:` is where the layout below switches to two columns, so it is
                        // the same boundary the design changes at.
                        //
                        // Only the panel's DIRECT children space this way — the two-column grid, the
                        // order table, and the cart actions. Features and Specifications sit inside the
                        // grid's right column, which stays a block formatting context, so their margin
                        // collapsing is untouched.
                        className="flex flex-col gap-10 outline-none lg:gap-8 focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2">
                        {/* `var(--spacing-16)` — 64px between the gallery and the copy column, per frame
                            2609447, at EVERY width. Confirmed on both parent frames: desktop
                            (`13610:46091`) puts the copy column at x=664 after a 600px gallery, and
                            mobile (`13869:82353`) starts it at y=463 under a 399px one — 64 in both. The
                            same declaration is the row gap while the columns are stacked below `lg` and
                            the column gap once they sit side by side. No `lg:` override: one value,
                            deliberately.

                            The columns are NOT halves. The desktop frame draws a 600px square gallery
                            against a 712px copy column inside 1376, so equal tracks made the gallery 56px
                            too wide and the copy 56px too narrow.

                            Expressed as a RATIO rather than as `[600px_1fr]`, which is the other reading
                            of the same frame. Both reproduce 600/712 exactly at 1376 — the only desktop
                            width the design defines — but `lg` starts at 1024, where a fixed 600px
                            gallery would leave the copy column ~360px and crush the specifications rows.
                            The design says nothing about 1024, so the ratio degrades it proportionally
                            instead of inventing a rule there.

                            `minmax(0, …)` on both tracks, not bare `fr`: an `fr` track's automatic
                            minimum is min-content, so the widest indivisible thing in either column —
                            an unbreakable spec value, an image's intrinsic width — would silently
                            override the ratio. */}
                        <div className="grid grid-cols-1 gap-16 lg:grid-cols-[minmax(0,600fr)_minmax(0,712fr)]">
                            {/* Left Column - Image Gallery */}
                            <div className="order-1">
                                <ImageGallery
                                    key={product.id}
                                    images={galleryImages}
                                    eager={!isProductASet && !isProductABundle}
                                    showNavigationArrows
                                    navigationArrowSize="lg"
                                    productName={product.name}
                                />
                                <UITarget targetId="sfcc.pdp.agent.productHelper" />
                            </div>

                            {/* Right Column - Description, specifications and remaining PDP sections */}
                            {/* The column stacks with a GAP of `var(--spacing-10)`, per Figma frame
                                2609453 — 40px between the Features copy and the Specifications
                                disclosure, at every width.

                                A gap rather than margins on the children, for the reason the margins
                                were carefully arranged before: a product with no Features copy must
                                leave no hole. `ProductFeatures` returns null and renders no DOM node, so
                                it becomes no flex item and the gap simply does not apply — the same
                                guarantee, without the collapsing arithmetic that made the rendered gap
                                32px where the design asks for 40. The four `UITarget`s below are gated
                                off and likewise render nothing, so they cost no gap either. */}
                            <div className="order-2 flex flex-col gap-10">
                                {/* FEATURES — bare prose, no label and no disclosure, unlike the
                                    Specifications chevron below it. The design names the block only in
                                    the layer name, never on the page.

                                    `fallback={null}` WHILE THE HYDRATION FLICKER IS OPEN. A deferred value
                                    that settles after hydration begins makes React discard the
                                    server-rendered content and render this boundary's fallback again, so
                                    the skeleton appears AFTER the copy already showed. `c_featuresam` is
                                    null on every product inspected, so in practice that was a skeleton
                                    flickering over empty space.

                                    Restore this fallback when the flicker is fixed — kept verbatim rather
                                    than described, so restoring is a copy back in. It also needs the
                                    `Skeleton` import returned to this file. Tracked against CRCB2B-313.

                                        <div className="space-y-2">
                                            <Skeleton className="h-3 w-full" />
                                            <Skeleton className="h-3 w-full" />
                                            <Skeleton className="h-3 w-2/3" />
                                        </div>
                                */}
                                {featuresPromise && (
                                    <Suspense fallback={null}>
                                        <ProductFeatures promise={featuresPromise} />
                                    </Suspense>
                                )}

                                {/* SPECIFICATIONS — a union across every variant of the master, not a read
                                    of this product. A variant URL and its master URL therefore render an
                                    identical panel.

                                    The WHOLE SECTION is gated on having rows, heading included — see
                                    `ProductSpecificationsSection`. The fallback renders the same disclosure
                                    shell so the expanded panel reserves the height its rows will take;
                                    rows arriving into a collapsed box would shift everything beneath them.

                                    This boundary KEEPS its skeleton, unlike the Features one above. The
                                    hydration flicker applies here too, but a fallback cannot see whether
                                    rows will resolve, so dropping it would remove the height reservation on
                                    the case that DOES resolve — 12 rows on the provisioned sandbox — and
                                    trade the flicker for a layout shift in a panel expanded above the fold.
                                    Task 8.6 and rule 8 of CLAUDE.md both point at keeping it. The flicker
                                    is tracked against CRCB2B-313 and belongs to that fix, not to this
                                    fallback.

                                    No padding overrides: `ui/accordion.tsx` already ships every
                                    `var(--spacing-6)` this design marks, so adding any would double them. */}
                                {orderPromise && (
                                    <Suspense
                                        fallback={
                                            <SpecificationsDisclosure label={t('specifications')}>
                                                <ProductSpecificationsFallback />
                                            </SpecificationsDisclosure>
                                        }>
                                        <ProductSpecificationsSection
                                            promise={orderPromise}
                                            label={t('specifications')}
                                        />
                                    </Suspense>
                                )}

                                {/* Kept while switched off, one `"enabled": false` per entry in each
                                    extension's `target-config.json` — all four are demo extensions serving the
                                    same fixture for every product, and none is in the approved design. Keeping
                                    the lines makes re-enabling a one-word JSON edit.

                                    The gate is per-targetId and GLOBAL, not per-page. These four happen to have
                                    one call site each — this one — so "off" and "off on the PDP" coincide today;
                                    a second surface mounting the same id would inherit the gate silently. */}
                                <UITarget targetId="sfcc.pdp.returnsWarranty" />
                                {/* @sfdc-extension-block-start SFDC_EXT_SHIPPING_DELIVERY */}
                                <UITarget targetId="sfcc.pdp.estimatedDelivery" />
                                {/* @sfdc-extension-block-end SFDC_EXT_SHIPPING_DELIVERY */}
                                <UITarget targetId="sfcc.pdp.faq" />
                                <UITarget targetId="sfcc.pdp.collapsibles" />
                            </div>
                        </div>

                        {/* ORDER — the full-width band below the two-column layout, where Figma puts the
                            Order block.

                            The table is the purchase path for a model: a buyer orders across several
                            variants in one pass, so quantity is per row rather than page-level. Its
                            columns are derived from the values that differ across the variant set, so a
                            category nobody configured still renders correctly.

                            The table now carries its own order action — one submission for every row
                            holding a quantity, with a running total that names the price it summed — so
                            the whole-product `<ProductCartActions>` no longer stands beside it. Two
                            purchase paths on one page is a question about which one is real, and the
                            answer for a model is the table.

                            It is an EITHER/OR rather than a removal, because the table does not render
                            for a set or a bundle: those still need a whole-product action, and so does
                            any surface composing this view without the order band. `hideOptionsMessage`
                            stays for them — with no swatches here a variant cannot be resolved, and the
                            prompt would ask for a selection nothing on this surface can make.

                            Still absent from the table, and absent rather than stubbed: MSRP/MAP and
                            Wholesale figures, each needing its own price book, and the `Save to List`
                            action beside the order button, whose behaviour is undefined. */}
                        {orderPromise && !isProductASet && !isProductABundle ? (
                            <Suspense fallback={<ProductVariationsTableFallback />}>
                                <ProductVariationsSection promise={orderPromise} product={product} />
                            </Suspense>
                        ) : (
                            <ProductCartActions product={product} hideExpressPayments hideOptionsMessage />
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </ProductViewProvider>
    );
}
